import {
  Badge,
  Button,
  Checkbox,
  cn,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import { AlertTriangleIcon, ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import { Fragment, useCallback, useState, type ReactNode } from "react";
import type { InstructorSessionByInstructorItemDTO } from "../api/client";
import {
  buildInstructorPayoutKindBreakdowns,
  formatHours,
  formatPayoutKip,
  getInstructorPayoutRateWarnings,
  hasInstructorPayoutRateIssue,
  type InstructorPayoutKindBreakdown,
} from "../lib/instructor-payout-display";
import type { InstructorPayrollRowSelection } from "./InstructorSessionReportTable";
import { PayoutQuotaRowHint } from "./instructor-payout-quota-display";

type InstructorMonthlyPayoutCompactTableProps = {
  isLoading: boolean;
  data: InstructorSessionByInstructorItemDTO[];
  footerNote?: string;
  renderRowActions?: (row: InstructorSessionByInstructorItemDTO) => ReactNode;
  payrollSelection?: InstructorPayrollRowSelection | null;
  onOpenDetail?: (row: InstructorSessionByInstructorItemDTO) => void;
  emptyMessage?: string;
};

function KindBreakdownCard({ item }: { item: InstructorPayoutKindBreakdown }) {
  const hasPrivateNearFar =
    item.key === "private_lesson" &&
    (Boolean(item.offSiteNearSessions) ||
      Boolean(item.offSiteFarSessions) ||
      Boolean(item.offSiteNearHours) ||
      Boolean(item.offSiteFarHours));
  const hasLocationSplit =
    !hasPrivateNearFar &&
    (item.studioSessions != null ||
      item.offSiteSessions != null ||
      item.studioHours != null);

  return (
    <div className="rounded-lg border bg-background p-3">
      <div className="mb-2 flex items-start justify-between gap-2">
        <span className="font-medium text-sm">{item.title}</span>
        <span className="font-semibold text-sm tabular-nums">
          {formatPayoutKip(item.payoutKip)}
        </span>
      </div>
      <div className="space-y-1 text-muted-foreground text-xs">
        <div className="tabular-nums">
          {item.sessions.toLocaleString("lo-LA")} ຄັ້ງ ·{" "}
          {formatHours(item.hours)} ຊມ.
        </div>
        {hasPrivateNearFar ? (
          <>
            <div className="tabular-nums">
              Studio: {item.studioSessions?.toLocaleString("lo-LA") ?? "—"}{" "}
              ຄັ້ງ · {formatHours(item.studioHours ?? 0)} ຊມ.
              {item.studioPayoutKip != null && item.studioPayoutKip > 0
                ? ` · ${formatPayoutKip(item.studioPayoutKip)}`
                : ""}
            </div>
            <div className="tabular-nums text-sky-800 dark:text-sky-300">
              ໃກ້: {item.offSiteNearSessions?.toLocaleString("lo-LA") ?? "—"}{" "}
              ຄັ້ງ · {formatHours(item.offSiteNearHours ?? 0)} ຊມ.
              {item.offSiteNearPayoutKip != null && item.offSiteNearPayoutKip > 0
                ? ` · ${formatPayoutKip(item.offSiteNearPayoutKip)}`
                : ""}
            </div>
            <div className="tabular-nums text-violet-800 dark:text-violet-300">
              ໄກ: {item.offSiteFarSessions?.toLocaleString("lo-LA") ?? "—"}{" "}
              ຄັ້ງ · {formatHours(item.offSiteFarHours ?? 0)} ຊມ.
              {item.offSiteFarPayoutKip != null && item.offSiteFarPayoutKip > 0
                ? ` · ${formatPayoutKip(item.offSiteFarPayoutKip)}`
                : ""}
            </div>
            {(item.rateStudio || item.rateOffSiteNear || item.rateOffSiteFar) && (
              <div>
                ອັດຕາ Studio {item.rateStudio ?? "—"}
                {item.rateOffSiteNear
                  ? ` · ໃກ້ ${item.rateOffSiteNear}`
                  : ""}
                {item.rateOffSiteFar ? ` · ໄກ ${item.rateOffSiteFar}` : ""}
              </div>
            )}
          </>
        ) : hasLocationSplit ? (
          <>
            <div className="tabular-nums">
              Studio: {item.studioSessions?.toLocaleString("lo-LA") ?? "—"}{" "}
              ຄັ້ງ · {formatHours(item.studioHours ?? 0)} ຊມ.
              {item.studioPayoutKip != null && item.studioPayoutKip > 0
                ? ` · ${formatPayoutKip(item.studioPayoutKip)}`
                : ""}
            </div>
            <div className="tabular-nums">
              ນອກສະຖານທີ່:{" "}
              {item.offSiteSessions?.toLocaleString("lo-LA") ?? "—"} ຄັ້ງ ·{" "}
              {formatHours(item.offSiteHours ?? 0)} ຊມ.
              {item.offSitePayoutKip != null && item.offSitePayoutKip > 0
                ? ` · ${formatPayoutKip(item.offSitePayoutKip)}`
                : ""}
            </div>
            {(item.rateStudio || item.rateOffSite) && (
              <div>
                ອັດຕາ Studio {item.rateStudio ?? "—"}
                {item.rateOffSite ? ` · ນອກສະຖານທີ່ ${item.rateOffSite}` : ""}
              </div>
            )}
          </>
        ) : item.rateStudio ? (
          <div>ອັດຕາ {item.rateStudio}</div>
        ) : null}
        {item.footnote ? (
          <p className="text-[11px] leading-snug">{item.footnote}</p>
        ) : null}
      </div>
    </div>
  );
}

function SessionBadges({ row }: { row: InstructorSessionByInstructorItemDTO }) {
  const nearPl = row.offSiteNearPrivateLessonSessions ?? 0;
  const farPl = row.offSiteFarPrivateLessonSessions ?? 0;
  const studioPl = row.studioPrivateLessonSessions ?? 0;

  return (
    <div className="flex flex-wrap gap-1">
      {row.generalClassSessions > 0 ? (
        <Badge variant="secondary" className="text-[10px]">
          ຄລາດສ໌ {row.generalClassSessions}
        </Badge>
      ) : null}
      {studioPl > 0 ? (
        <Badge variant="outline" className="text-[10px]">
          ສ່ວນຕົວ Studio {studioPl}
        </Badge>
      ) : null}
      {nearPl > 0 ? (
        <Badge
          variant="outline"
          className="border-sky-500/40 bg-sky-500/10 text-[10px] text-sky-800"
        >
          ໃກ້ {nearPl}
        </Badge>
      ) : null}
      {farPl > 0 ? (
        <Badge
          variant="outline"
          className="border-violet-500/40 bg-violet-500/10 text-[10px] text-violet-800"
        >
          ໄກ {farPl}
        </Badge>
      ) : null}
      {row.privateLessonSessions > 0 &&
      studioPl === 0 &&
      nearPl === 0 &&
      farPl === 0 ? (
        <Badge variant="outline" className="text-[10px]">
          ສ່ວນຕົວ {row.privateLessonSessions}
        </Badge>
      ) : null}
      {row.partnerInstructorOnlyClassSessions > 0 ? (
        <Badge variant="outline" className="border-dashed text-[10px]">
          Partner {row.partnerInstructorOnlyClassSessions}
        </Badge>
      ) : null}
      {(row.roomRentalAssistSessions ?? 0) > 0 ? (
        <Badge variant="outline" className="text-[10px]">
          ຊ່ວຍສອນເຊົ່າຫ້ອງ {row.roomRentalAssistSessions}
        </Badge>
      ) : null}
      {(row.generalClassAssistSessions ?? 0) > 0 ? (
        <Badge variant="outline" className="text-[10px]">
          TA {row.generalClassAssistSessions}
        </Badge>
      ) : null}
    </div>
  );
}

function RateIssueHint({ row }: { row: InstructorSessionByInstructorItemDTO }) {
  const warnings = getInstructorPayoutRateWarnings(row);
  if (warnings.length === 0) return null;

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      <Badge
        variant="outline"
        className="gap-0.5 border-amber-500/50 bg-amber-500/10 text-[10px] text-amber-900"
        title={warnings.join(" · ")}
      >
        <AlertTriangleIcon className="size-3" aria-hidden />
        ບໍ່ມີອັດຕາ
      </Badge>
      <Link
        to="/app/master/instructors/$id/edit"
        params={{ id: row.instructorId }}
        className="text-[10px] text-primary hover:underline"
      >
        ແກ້ອັດຕາ
      </Link>
    </div>
  );
}

export function InstructorMonthlyPayoutCompactTable({
  isLoading,
  data,
  footerNote,
  renderRowActions,
  payrollSelection,
  onOpenDetail,
  emptyMessage = "ບໍ່ພົບຂໍ້ມູນໃນຊ່ວງວັນທີ່ເລືອກ",
}: InstructorMonthlyPayoutCompactTableProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const hasPayroll = payrollSelection != null;

  const toggleExpanded = useCallback((instructorId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(instructorId)) next.delete(instructorId);
      else next.add(instructorId);
      return next;
    });
  }, []);

  const allPayrollSelected =
    hasPayroll &&
    data.length > 0 &&
    data.every((r) => payrollSelection.selectedIds.has(r.instructorId));
  const somePayrollSelected =
    hasPayroll &&
    data.some((r) => payrollSelection.selectedIds.has(r.instructorId));

  const baseColCount =
    5 + (hasPayroll ? 1 : 0) + (renderRowActions ? 1 : 0);

  const renderMobileCards = () => {
    if (isLoading) {
      return (
        <p className="py-8 text-center text-muted-foreground text-sm">
          ກຳລັງໂຫລດ...
        </p>
      );
    }
    if (data.length === 0) {
      return (
        <p className="py-8 text-center text-muted-foreground text-sm">
          {emptyMessage}
        </p>
      );
    }
    return data.map((row) => {
      const expanded = expandedIds.has(row.instructorId);
      const breakdowns = buildInstructorPayoutKindBreakdowns(row);
      const rateIssue = hasInstructorPayoutRateIssue(row);

      return (
        <div
          key={row.instructorId}
          className={cn(
            "rounded-lg border bg-card p-3 shadow-sm",
            rateIssue && "border-amber-500/40 bg-amber-500/5",
          )}
        >
          <div className="flex items-start gap-2">
            {hasPayroll ? (
              <Checkbox
                className="mt-1"
                checked={payrollSelection.selectedIds.has(row.instructorId)}
                onCheckedChange={(val) => {
                  payrollSelection.onToggle(row.instructorId, Boolean(val));
                }}
                aria-label={`ເລືອກບັນທຶກ ${row.instructorName}`}
              />
            ) : null}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div
                  className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary text-[10px]"
                  aria-hidden
                >
                  {row.instructorName.trim().charAt(0).toUpperCase() || "?"}
                </div>
                <button
                  type="button"
                  className="truncate text-left font-medium text-sm hover:underline"
                  onClick={() => onOpenDetail?.(row)}
                >
                  {row.instructorName}
                </button>
                {row.bankAccountNumber ? (
                  <p className="truncate text-muted-foreground text-xs tabular-nums">
                    {row.bankAccountNumber}
                  </p>
                ) : null}
              </div>
              <div className="mt-1">
                <PayoutQuotaRowHint row={row} className="mb-1" />
                <SessionBadges row={row} />
                <RateIssueHint row={row} />
              </div>
              <div className="mt-2 flex justify-between text-sm tabular-nums">
                <span className="text-muted-foreground">
                  {row.sessionsTaught.toLocaleString("lo-LA")} ຄັ້ງ ·{" "}
                  {formatHours(row.billableHoursTotal)} ຊມ.
                </span>
                <span className="font-semibold">
                  {formatPayoutKip(row.estimatedPayout)}
                </span>
              </div>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2"
              onClick={() => toggleExpanded(row.instructorId)}
            >
              {expanded ? "ຫຍໍ້" : "ແຍກຕາມປະເພດ"}
            </Button>
            {renderRowActions ? renderRowActions(row) : null}
          </div>
          {expanded ? (
            <div className="mt-3 grid gap-2">
              {breakdowns.map((item) => (
                <KindBreakdownCard key={item.key} item={item} />
              ))}
            </div>
          ) : null}
        </div>
      );
    });
  };

  return (
    <div className="space-y-2">
      <div className="space-y-3 md:hidden">{renderMobileCards()}</div>

      <div className="hidden overflow-x-auto rounded-md border md:block">
        <Table>
          <TableHeader>
            <TableRow>
              {hasPayroll ? (
                <TableHead className="w-10">
                  <Checkbox
                    checked={
                      allPayrollSelected
                        ? true
                        : somePayrollSelected
                          ? "indeterminate"
                          : false
                    }
                    onCheckedChange={(val) => {
                      payrollSelection.onSelectAll(val === true);
                    }}
                    aria-label="ເລືອກຄູທັງໝົດ"
                  />
                </TableHead>
              ) : null}
              <TableHead className="w-10" />
              <TableHead className="min-w-[160px]">ຄູສອນ</TableHead>
              <TableHead className="min-w-[140px]">ຄັ້ງ</TableHead>
              <TableHead className="w-[88px] text-right">ຊົ່ວໂມງ</TableHead>
              <TableHead className="min-w-[120px] text-right">
                ຄ່າຈ້າງ (ກີບ)
              </TableHead>
              {renderRowActions ? (
                <TableHead className="min-w-[96px] text-right">
                  ລາຍລະອຽດ
                </TableHead>
              ) : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={baseColCount}
                  className="h-24 text-center text-muted-foreground"
                >
                  ກຳລັງໂຫລດ...
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={baseColCount}
                  className="h-24 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => {
                const expanded = expandedIds.has(row.instructorId);
                const breakdowns = buildInstructorPayoutKindBreakdowns(row);
                const rateIssue = hasInstructorPayoutRateIssue(row);

                return (
                  <Fragment key={row.instructorId}>
                    <TableRow
                      className={cn(
                        expanded ? "border-b-0" : undefined,
                        rateIssue && "bg-amber-500/5 hover:bg-amber-500/10",
                      )}
                    >
                      {hasPayroll ? (
                        <TableCell className="w-10 align-middle">
                          <Label
                            htmlFor={`payroll-row-${row.instructorId}`}
                            className="sr-only"
                          >
                            ເລືອກບັນທຶກ {row.instructorName}
                          </Label>
                          <Checkbox
                            id={`payroll-row-${row.instructorId}`}
                            checked={payrollSelection.selectedIds.has(
                              row.instructorId,
                            )}
                            onCheckedChange={(val) => {
                              payrollSelection.onToggle(
                                row.instructorId,
                                Boolean(val),
                              );
                            }}
                          />
                        </TableCell>
                      ) : null}
                      <TableCell className="w-10 align-middle">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-8"
                          onClick={() => toggleExpanded(row.instructorId)}
                          aria-expanded={expanded}
                          aria-label={
                            expanded ? "ຫຍໍ້ລາຍລະອຽດ" : "ຂະຫຍາຍລາຍລະອຽດ"
                          }
                        >
                          {expanded ? (
                            <ChevronDownIcon className="size-4" />
                          ) : (
                            <ChevronRightIcon className="size-4" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-start gap-2.5">
                          <div
                            className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary text-xs"
                            aria-hidden
                          >
                            {row.instructorName.trim().charAt(0).toUpperCase() ||
                              "?"}
                          </div>
                          <div className="min-w-0">
                            {onOpenDetail ? (
                              <button
                                type="button"
                                className="block truncate text-left font-medium text-sm hover:underline"
                                onClick={() => onOpenDetail(row)}
                              >
                                {row.instructorName}
                              </button>
                            ) : (
                              <span className="block truncate text-sm">
                                {row.instructorName}
                              </span>
                            )}
                            {row.bankAccountNumber ? (
                              <p className="truncate text-muted-foreground text-xs tabular-nums">
                                {row.bankAccountNumber}
                              </p>
                            ) : null}
                            <RateIssueHint row={row} />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <span className="font-medium text-sm tabular-nums">
                            {row.sessionsTaught.toLocaleString("lo-LA")}
                          </span>
                          <PayoutQuotaRowHint row={row} className="mb-1" />
                          <SessionBadges row={row} />
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        {formatHours(row.billableHoursTotal)}
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">
                        {formatPayoutKip(row.estimatedPayout)}
                      </TableCell>
                      {renderRowActions ? (
                        <TableCell className="text-right">
                          {renderRowActions(row)}
                        </TableCell>
                      ) : null}
                    </TableRow>
                    {expanded ? (
                      <TableRow className="bg-muted/20 hover:bg-muted/20">
                        <TableCell
                          colSpan={baseColCount}
                          className="px-4 py-3"
                        >
                          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                            {breakdowns.map((item) => (
                              <KindBreakdownCard key={item.key} item={item} />
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      {footerNote ? (
        <p className="text-muted-foreground text-xs leading-relaxed">
          {footerNote}
        </p>
      ) : null}
    </div>
  );
}
